import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import StudentElementsUI from "./StudentElementsUI";

const StudentElementsTab = ({ selectedStudent }) => {
  const token = localStorage.getItem("token");
  const studentId = selectedStudent.id;
  const rollNo = selectedStudent.rollNo;
  const [elementsData, setElementsData] = useState([]);
  const [fileCounts, setFileCounts] = useState({});
  const [fileUrls, setFileUrls] = useState({});

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      elements: [
        {
          name: "",
          description: "",
          createdBy: localStorage.getItem("userId"),
          elementDetail: {
            value: "",
            description: "",
            effectiveDate: null,
            endDate: null,
            createdBy: localStorage.getItem("userId"),
            fileName: "",
            filePath: "",
            base64String: "",
          },
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "elements",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch elements dropdown data
        const elementsResponse = await get(
          `Dropdown/GetElements?StudentId=${rollNo}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setElementsData(elementsResponse);

        // Fetch existing student elements
        const studentElementsData = await get(
          `InspStudentFirstRegistrtion/GetStudentElementByStudentId?studentId=${studentId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (studentElementsData) {
          const newFileCounts = {};
          const newFileUrls = {};
          
          const mappedElements = studentElementsData?.length > 0
            ? studentElementsData.map((element, index) => {
                const matchingElement = elementsResponse.find(
                  (item) => item.id === element.elementId
                );

                const filePath = element.filePath ? (element.filePath.startsWith("/") ? element.filePath : `/${element.filePath}`) : "";
                const fileUrl = filePath && element.fileName ? `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${filePath}` : "";

                newFileCounts[index] = element.fileName ? 1 : 0;
                newFileUrls[index] = fileUrl;

                return {
                  id: element.id || 0,
                  value: element.value || "",
                  name: matchingElement
                    ? { value: matchingElement.id, label: matchingElement.name }
                    : null,
                  description: element.description || "",
                  elementDetail: {
                    value: element.value || "",
                    description: element.description || "",
                    effectiveDate: element.effectiveDate?.split("T")[0] || null,
                    endDate: element.endDate?.split("T")[0] || null,
                    fileName: element.fileName || "",
                    filePath: element.filePath || "",
                    base64String: element.base64String || "",
                  },
                };
              })
            : [
                {
                  name: "",
                  description: "",
                  createdBy: localStorage.getItem("userId"),
                  elementDetail: {
                    value: "",
                    description: "",
                    effectiveDate: null,
                    endDate: null,
                    createdBy: localStorage.getItem("userId"),
                    fileName: "",
                    filePath: "",
                    base64String: "",
                  },
                },
              ];

          setFileCounts(newFileCounts);
          setFileUrls(newFileUrls);

          reset({
            elements: mappedElements,
          });
        }
      } catch (error) {
        console.error("Error fetching student elements data:", error);
      }
    };

    if (studentId && token) {
      fetchData();
    }
  }, [studentId, token, reset]);

  const handleAttachmentFileChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      setFileCounts((prevCounts) => ({ ...prevCounts, [index]: 1 }));
      setValue(`elements[${index}].elementDetail.fileName`, file.name, {
        shouldValidate: true,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue(
          `elements[${index}].elementDetail.base64String`,
          base64String,
          { shouldValidate: true }
        );
        const fileUrl = URL.createObjectURL(file);
        setFileUrls((prevUrls) => ({ ...prevUrls, [index]: fileUrl }));
      };
      reader.readAsDataURL(file);
    } else {
      setFileCounts((prevCounts) => ({ ...prevCounts, [index]: 0 }));
      setValue(`elements[${index}].elementDetail.fileName`, "", {
        shouldValidate: true,
      });
      setValue(`elements[${index}].elementDetail.base64String`, "", {
        shouldValidate: true,
      });
      setValue(`elements[${index}].elementDetail.filePath`, "", {
        shouldValidate: true,
      });
      setFileUrls((prevUrls) => ({ ...prevUrls, [index]: "" }));
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized");
      return;
    }

    const elementsDataPayload = data?.elements
      ?.map((element) => {
        if (!element.name || element.name.value === undefined) {
          return null;
        }

        return {
          id: element.id || 0,
          name: element.name?.label || element.name || "",
          description: element.description || "",
          createdBy:localStorage.getItem("userId"),
          elementDetail: {
            elementId: element.name.value || 0,
            value: element.elementDetail?.value || "",
            description: element.elementDetail?.description || "",
            effectiveDate: element.elementDetail?.effectiveDate || null,
            endDate: element.elementDetail?.endDate || null,
            fileName: element.elementDetail?.fileName || "",
            filePath: element.elementDetail?.filePath || "",
            base64String: element.elementDetail?.base64String || "",
            createdBy:localStorage.getItem("userId"),
          },
        };
      })
      .filter((element) => element !== null);

    try {
      const response = await post(`InspStudentFirstRegistrtion/AddStudentElements?studentId=${studentId}`, elementsDataPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success("Elements updated successfully!");
      } else {
        toast.error(response.message || "Failed to update elements.");
      }
    } catch (error) {
      console.error("Error saving student elements:", error);
      toast.error("An error occurred while saving.");
    }
  };

  return (
    <div className="profile-container p-4">
      <div className="tab-header mb-4">
        <h4 className="tab-title">Student Elements</h4>
        <p className="tab-subtitle text-muted">Manage additional attributes and custom data points for this student.</p>
      </div>

      <div className="profile-content-scrollable">
        <form onSubmit={handleSubmit(saveData)}>
          <StudentElementsUI
            fields={fields}
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            elementsData={elementsData}
            append={append}
            remove={remove}
            handleAttachmentFileChange={handleAttachmentFileChange}
            fileCounts={fileCounts}
            fileUrls={fileUrls}
            setValue={setValue}
            getValues={getValues}
            trigger={trigger}
          />

          <div className="tab-footer mt-5 d-flex justify-content-end">
            <button
              className="btn btn-save-elements"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : <i className="fas fa-save me-2"></i>}
              Save All Changes
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .profile-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background-color: #ffffff;
        }

        .tab-title {
          font-weight: 800;
          color: #1a202c;
          margin-bottom: 0.25rem;
          letter-spacing: -0.02em;
        }

        .tab-subtitle {
          font-size: 0.85rem;
          margin-bottom: 0;
        }

        .profile-content-scrollable {
          flex-grow: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 8px;
        }

        .profile-content-scrollable::-webkit-scrollbar {
          width: 6px;
        }

        .profile-content-scrollable::-webkit-scrollbar-track {
          background: #f7fafc;
        }

        .profile-content-scrollable::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }

        .btn-save-elements {
          background: #2d3748;
          color: white;
          border: none;
          padding: 0.75rem 2.5rem;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          box-shadow: 0 4px 6px rgba(45, 55, 72, 0.15);
        }

        .btn-save-elements:hover {
          background: #1a202c;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(45, 55, 72, 0.25);
        }

        .btn-save-elements:active {
          transform: translateY(0);
        }

        .btn-save-elements:disabled {
          background: #a0aec0;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default StudentElementsTab;
